import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminUsers() {
  const { getToken } = useAuth();
  const [data, setData] = useState({ roles: [], users: [] });
  const [busy, setBusy] = useState(null);

  const load = () => makeApi(getToken).get("/users").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [getToken]);

  const changeRole = async (userId, role) => {
    setBusy(userId);
    try {
      await makeApi(getToken).patch(`/users/${userId}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Users & roles</h1>
        <p className="text-slate-600 mt-2 text-sm">Only this environment's users are listed. At least one admin is always kept.</p>
      </div>
      <Card className="p-0 border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Subjects</TableHead>
              <TableHead className="w-48">Set role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                <TableCell className="text-slate-900">{u.email || "—"}</TableCell>
                <TableCell className="text-slate-700">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={u.role === "admin" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}
                  >
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{u.selected_subjects?.length || 0}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)} disabled={busy === u.user_id}>
                    <SelectTrigger data-testid={`admin-user-role-select-${u.user_id}`} className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {data.roles.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {!data.users.length && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-slate-500 text-sm">No users yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
